/**
 * Summary Step - Visual recap with completion %, edit links, no sample data
 */

import React from 'react';
import {
  Building2, Truck, Users, Shield, MapPin, Sparkles, Wrench,
  AlertCircle, CheckCircle2, Edit3, ArrowRight
} from 'lucide-react';

function CompletionBadge({ filled, total, label }) {
  if (total === 0) return null;
  const pct = Math.round((filled / total) * 100);
  const color = pct >= 80 ? 'text-green-600 bg-green-50' : pct >= 40 ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {filled}/{total} {label}
    </span>
  );
}

export function SummaryStep({ data, updateData, onBack, onGenerate, isGenerating, error, goToStep }) {
  const vehicles = data.vehicles || [];
  const drivers = data.drivers || [];
  const materials = data.materials || [];
  const sites = data.sites || [];
  const categories = data.vehicleCategories || {};

  const totalVehicles = Object.values(categories).reduce((sum, v) => sum + v, 0);
  const filledVehicles = vehicles.filter(v => v.license_plate || v.make).length;

  // Build category summary
  const catLabels = { VL: 'VL', VU: 'VU', PL: 'PL', ENGIN_CHANTIER: 'Engins', MOTO: 'Motos' };
  const catSummary = Object.entries(categories)
    .filter(([_, count]) => count > 0)
    .map(([id, count]) => `${count} ${catLabels[id] || id}`)
    .join(', ');

  // Certifications summary
  const certLabels = { caces: 'CACES', adr: 'ADR', fimo: 'FIMO' };
  const certSummary = (data.certifications || [])
    .map(c => certLabels[c] || c)
    .join(', ');

  // Summary sections
  const sections = [
    {
      icon: Building2,
      label: 'Entreprise',
      value: data.companyName || 'Non renseigné',
      subvalue: [data.sector, data.companyCity].filter(Boolean).join(' - ') || null,
      color: 'bg-blue-500',
      stepIndex: 0,
    },
    {
      icon: MapPin,
      label: 'Sites',
      value: `${sites.length} site${sites.length > 1 ? 's' : ''}`,
      subvalue: sites.map(s => s.name).join(', ') || null,
      color: 'bg-indigo-500',
      stepIndex: 1,
    },
    {
      icon: Shield,
      label: 'Assurance',
      value: data.insurerName || 'Non configuré',
      subvalue: data.brokerName ? `via ${data.brokerName}` : null,
      color: 'bg-emerald-500',
      stepIndex: 2,
    },
    {
      icon: Truck,
      label: 'Véhicules',
      value: totalVehicles > 0 ? `${totalVehicles} véhicule${totalVehicles > 1 ? 's' : ''}` : 'Aucun',
      subvalue: catSummary || null,
      color: 'bg-green-500',
      stepIndex: 3,
      badge: totalVehicles > 0 ? <CompletionBadge filled={filledVehicles} total={totalVehicles} label="renseignés" /> : null,
    },
    {
      icon: Users,
      label: 'Conducteurs',
      value: drivers.length > 0 ? `${drivers.length} conducteur${drivers.length > 1 ? 's' : ''}` : 'Aucun',
      subvalue: certSummary || null,
      color: 'bg-purple-500',
      stepIndex: 5,
    },
    {
      icon: Wrench,
      label: 'Matériel',
      value: materials.length > 0 ? `${materials.length} équipement${materials.length > 1 ? 's' : ''}` : 'Non configuré',
      subvalue: null,
      color: 'bg-orange-500',
      stepIndex: 6,
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Récapitulatif
        </h2>
        <p className="text-gray-500">
          Vérifiez les informations avant d'activer votre flotte
        </p>
      </div>

      {/* Summary card */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 space-y-3">
          {sections.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={index}
                className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl group"
              >
                <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-gray-400 text-sm">{item.label}</p>
                    {item.badge}
                  </div>
                  <p className="text-gray-900 font-medium">{item.value}</p>
                  {item.subvalue && (
                    <p className="text-gray-400 text-sm truncate">{item.subvalue}</p>
                  )}
                </div>
                {/* Edit link */}
                {goToStep && (
                  <button
                    onClick={() => goToStep(item.stepIndex)}
                    className="p-2 text-gray-300 hover:text-amber-500 transition-colors opacity-0 group-hover:opacity-100"
                    title="Modifier"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* What will be created */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h4 className="text-amber-700 font-medium flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" />
          Ce qui sera créé :
        </h4>
        <ul className="text-gray-600 text-sm space-y-1">
          <li className="flex items-center gap-2">
            <ArrowRight className="w-3 h-3 text-amber-500" />
            1 organisation avec vos informations
          </li>
          {sites.length > 0 && (
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 text-amber-500" />
              {sites.length} site{sites.length > 1 ? 's' : ''} de travail
            </li>
          )}
          {totalVehicles > 0 && (
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 text-amber-500" />
              {totalVehicles} véhicule{totalVehicles > 1 ? 's' : ''} ({catSummary})
            </li>
          )}
          {drivers.length > 0 && (
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 text-amber-500" />
              {drivers.length} conducteur{drivers.length > 1 ? 's' : ''} avec leurs affectations
            </li>
          )}
          {data.insurerName && (
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 text-amber-500" />
              1 contrat d'assurance flotte
            </li>
          )}
          {materials.length > 0 && (
            <li className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 text-amber-500" />
              {materials.length} matériel{materials.length > 1 ? 's' : ''} / outil{materials.length > 1 ? 's' : ''}
            </li>
          )}
        </ul>
      </div>

      {/* Edit hint */}
      <div className="text-center">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-gray-600 text-sm underline transition-colors"
        >
          Modifier les informations
        </button>
      </div>
    </div>
  );
}

export default SummaryStep;
